// Smaller menu features: function categories (Insert > Function), smart chips (file, rating, display format),
// the image insert dialog, accessibility announcements, help / updates / feedback, security restrictions.
import { functionNames, indexToCol } from '../../engine/src/index.ts';
import type { Op } from '../../shared/ops.ts';
import { api } from './api.ts';
import type { DocSummary } from './api.ts';
import fnCats from './fn-cats.json';
import { insertImageInCell, insertImageOver } from './objects.ts';
import type { ObjectLayer } from './objects.ts';
import type { PanelCtx } from './panels.ts';
import { dialog, h, toast } from './ui.ts';
import { logo } from './logo.ts';
import type { MenuItem } from './ui.ts';

export const DEV = ' (개발 중)';
/** A Google-only feature: shown like in Sheets, but not available here. */
export const dev = (label: string, shortcut?: string): MenuItem => ({ label: label + DEV, shortcut, disabled: true });

// ----- Insert > Function: Sheets' categories -----
export function functionMenu(insert: (name: string) => void, more: () => void): MenuItem[] {
  const have = new Set(functionNames());
  const cats = fnCats as Record<string, string[]>;
  return [
    ...['SUM', 'AVERAGE', 'COUNT', 'MAX', 'MIN'].map(f => ({ label: f, action: () => insert(f) })),
    { separator: true },
    ...Object.entries(cats).map(([cat, names]) => ({ label: cat, items: names.map(n => (have.has(n) ? { label: n, action: () => insert(n) } : dev(n))) })),
    { separator: true },
    { label: '자세히 알아보기', action: more },
  ];
}

// ----- smart chips -----
/** Insert > Smart chips > File: a chip linking to another spreadsheet here. */
export async function insertFileChip(x: PanelCtx): Promise<void> {
  const docs = (await api<{ docs: DocSummary[] }>('GET', '/api/docs')).docs.filter(d => d.id !== x.docId);
  const q = h('input', { class: 'dlg-input', placeholder: '스프레드시트 검색' }), list = h('div', { class: 'search-list' });
  const render = (): void => list.replaceChildren(...docs.filter(d => !q.value.trim() || d.title.toLowerCase().includes(q.value.trim().toLowerCase())).slice(0, 30).map(d =>
    h('div', { class: 'search-item', onmousedown: () => { pick(d); } }, logo(16, 'doc-ic'), d.title, h('span', { class: 'muted' }, ` · ${d.owner_name}`))));
  const pick = (d: DocSummary): void => {
    q.closest('.dlg-back')?.remove();
    const { r, c } = x.grid.sel, s = x.sheet();
    x.submit({ t: 'batch', ops: [{ t: 'set', sheet: s, r, c, raw: d.title }, { t: 'fmt', sheet: s, r1: r, c1: c, r2: r, c2: c, fmt: { link: `${location.origin}/d/${d.id}`, chip: 'file' } }] });
    x.grid.el.focus();
  };
  q.addEventListener('input', render); render();
  dialog('파일 칩 삽입', h('div', { class: 'col' }, q, list), [{ label: '닫기', value: 'x' }]);
  setTimeout(() => q.focus());
}
/** Insert > Smart chips > Rating: 0-5 stars stored as a number. */
export function insertRating(x: PanelCtx): void {
  const R = x.grid.range, s = x.sheet(), ops: Op[] = [{ t: 'fmt', sheet: s, ...R, fmt: { nf: 'rating', ha: 'left' } }];
  for (let r = R.r1; r <= R.r2; r++) for (let c = R.c1; c <= R.c2; c++) if (!/^[0-5]$/.test(x.model.raw(s, r, c))) ops.push({ t: 'set', sheet: s, r, c, raw: '0' });
  x.submit({ t: 'batch', ops });
}
/** Format > Smart chips: how people chips show (default, name, email). */
export const chipDisplay = (x: PanelCtx, show: 'default' | 'name' | 'email'): void => x.submit({ t: 'fmt', sheet: x.sheet(), ...x.grid.range, fmt: { chipShow: show === 'default' ? null : show } });

// ----- Insert > Image (in a cell / over cells): upload or URL -----
export async function imageDialog(x: PanelCtx, layer: ObjectLayer, where: 'in' | 'over'): Promise<void> {
  const choice = await dialog(where === 'in' ? '셀 내에 이미지 삽입' : '이미지를 셀 위에 삽입', h('p', { class: 'muted' }, 'PNG, JPEG, GIF, WebP 이미지(10MB 이하)를 업로드하거나 이미지 주소(URL)를 입력하세요.'),
    [{ label: '취소', value: '' }, { label: 'URL로 삽입', value: 'url' }, { label: '업로드', value: 'upload', primary: true }]);
  if (!choice) return;
  if (where === 'in') await insertImageInCell(x, choice === 'url');
  else await insertImageOver(x, layer, choice === 'url');
}

// ----- Tools > Accessibility: screen reader announcements -----
let live: HTMLElement | null = null;
export const a11yOn = (): boolean => { try { return localStorage.getItem('sheet.a11y') === '1'; } catch { return false; } };
export async function accessibilityDialog(): Promise<void> {
  const cb = h('input', { type: 'checkbox', checked: a11yOn() });
  if (!await dialog('접근성 설정', h('div', { class: 'col' }, h('label', { class: 'row' }, cb, ' 스크린 리더 지원 사용 설정'), h('p', { class: 'muted' }, '선택한 셀의 주소와 내용, 편집 결과를 스크린 리더가 읽을 수 있도록 알려줍니다.')),
    [{ label: '취소', value: false }, { label: '확인', value: true, primary: true }])) return;
  try { localStorage.setItem('sheet.a11y', cb.checked ? '1' : '0'); } catch { /* storage unavailable */ }
  toast(cb.checked ? '스크린 리더 지원을 사용합니다' : '스크린 리더 지원을 사용하지 않습니다');
}
export function announce(text: string): void {
  if (!a11yOn()) return;
  if (!live) { live = h('div', { class: 'sr-only', 'aria-live': 'polite', role: 'status' }); document.body.append(live); }
  live.textContent = text;
}
export const cellAnnouncement = (r: number, c: number, text: string): string => `${indexToCol(c)}${r + 1}${text ? `, ${text}` : ', 비어 있음'}`;

// ----- Help -----
const HELP: [string, string][] = [
  ['시작하기', '홈에서 빈 스프레드시트나 템플릿으로 새 문서를 만들거나 .xlsx/.xls/.csv 파일을 업로드합니다. 공유 버튼으로 사내 사용자에게 편집자/댓글 작성자/뷰어 권한을 줄 수 있습니다.'],
  ['수식', '=로 시작해 입력합니다. 함수 이름을 입력하면 자동완성과 인수 도움말이 나타나고, 방향키나 클릭으로 참조를 넣을 수 있습니다. 함수 목록은 도움말 > 함수 목록에 있습니다.'],
  ['동시 편집', '같은 문서를 연 사람의 커서와 변경사항이 실시간으로 보입니다. 연결이 끊겨도 편집 내용은 다시 연결되면 저장됩니다.'],
  ['댓글과 메모', '셀을 선택하고 Ctrl+Alt+M으로 댓글을, Shift+F2로 메모를 추가합니다. 댓글에 @아이디를 쓰면 그 사람에게 메일이 갑니다.'],
  ['데이터', '필터, 필터 보기, 슬라이서, 데이터 확인(드롭다운), 조건부 서식, 피봇 테이블, 차트를 데이터·삽입·서식 메뉴에서 쓸 수 있습니다.'],
  ['버전 기록', '파일 > 버전 기록에서 이전 버전을 보고 복원합니다. 편집 중 30분마다 자동 저장되며 이름을 지정해 남길 수도 있습니다.'],
  ['가져오기/내보내기', '파일 > 가져오기로 .xlsx/.xls/.csv를, 파일 > 다운로드로 .xlsx/.ods/.pdf/.csv/.tsv/.html을 받을 수 있습니다.'],
  ['단축키', '도움말 > 키보드 단축키(Ctrl+/)에서 전체 목록을 볼 수 있습니다. 메뉴 검색은 Alt+/ 입니다.'],
];
export function helpDialog(): void {
  const q = h('input', { class: 'dlg-input', placeholder: '도움말 검색' }), list = h('div', { class: 'col help-list' });
  const render = (): void => { const s = q.value.trim(); list.replaceChildren(...HELP.filter(([t, b]) => !s || t.includes(s) || b.includes(s)).map(([t, b]) => h('div', { class: 'help-item' }, h('b', {}, t), h('p', {}, b)))); };
  q.addEventListener('input', render); render();
  dialog('스프레드시트 도움말', h('div', { class: 'col' }, q, list), [{ label: '닫기', value: 'x', primary: true }]);
}

export function mcpGuideDialog(documentTitle: string): void {
  const endpoint = `${location.origin}/mcp`;
  const documentUrl = `${location.origin}${location.pathname}`;
  const copy = async (text: string): Promise<void> => {
    try { await navigator.clipboard.writeText(text); toast('복사했습니다'); }
    catch { toast('복사하지 못했습니다. 텍스트를 선택해 직접 복사해 주세요'); }
  };
  const address = h('input', { id: 'mcp-server-url', class: 'dlg-input', type: 'text', value: endpoint, readOnly: true, onfocus: (e: FocusEvent) => (e.target as HTMLInputElement).select() });
  const provider = h('select', { id: 'mcp-provider', class: 'dlg-input' }, h('option', { value: 'chatgpt' }, 'ChatGPT'), h('option', { value: 'claude' }, 'Claude'));
  const setup = h('section', {}), links = h('div', { class: 'row wrap' });
  const renderSetup = (): void => {
    const chatgpt = provider.value === 'chatgpt';
    const steps = chatgpt ? [
      'ChatGPT 웹에서 플러그인(Plugins) → + → Add custom MCP server를 엽니다. 아래 ‘ChatGPT 플러그인 열기’ 링크로도 이동할 수 있습니다.',
      '이름은 Sheet, Server URL은 위 주소, 인증은 OAuth로 설정합니다. Client ID와 Secret은 비워 둡니다. 등록 방식 선택이 나오면 DCR(동적 클라이언트 등록)을 선택합니다. Google OAuth 클라이언트 정보는 사용하지 않습니다.',
      '위험 안내를 확인한 뒤 Create as a plugin을 누릅니다. Sheet 계정으로 로그인하고 ‘Sheet 연결 허용’ 화면에서 허용을 누릅니다. 로그인 요청 시점은 ChatGPT 화면에 따라 다를 수 있습니다.',
      '생성된 개인 또는 워크스페이스 플러그인 목록에서 Sheet를 설치(Install)합니다. 새 대화 입력창에서 @를 입력해 Sheet를 선택하고 요청하세요.',
    ] : [
      'Claude 웹 또는 데스크톱에서 설정 → 커넥터 → + 추가 → 커스텀 커넥터 추가를 엽니다. 버전에 따라 맞춤 설정(Customize) → 커넥터에 있습니다.',
      '이름은 Sheet, 서버 URL은 위 주소를 입력합니다. 고급 설정의 OAuth Client ID와 Secret은 비워 둡니다.',
      '추가 후 Sheet 계정으로 로그인하고, ‘Sheet 연결 허용’ 화면에서 허용을 누릅니다. Claude로 돌아오면 연결 상태를 확인합니다.',
      'Claude에서 새 대화를 열고 입력창의 + → 커넥터에서 Sheet를 켭니다. 커넥터 이름을 다르게 지정했다면 그 이름을 선택합니다.',
    ];
    setup.replaceChildren(h('h3', {}, `1. ${chatgpt ? 'ChatGPT' : 'Claude'}에 연결하기`), h('ol', {}, ...steps.map(step => h('li', {}, step))));
    const destinations = chatgpt ? [
      ['https://chatgpt.com/plugins', 'ChatGPT 플러그인 열기 ↗'],
      ['https://developers.openai.com/api/docs/guides/custom-mcp-server', 'OpenAI 공식 연결 도움말 ↗'],
    ] : [
      ['https://claude.ai/settings/connectors', 'Claude 커넥터 열기 ↗'],
      ['https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp', 'Claude 공식 연결 도움말 ↗'],
    ];
    links.replaceChildren(...destinations.map(([href, title]) => h('a', { href, target: '_blank', rel: 'noopener noreferrer' }, title)));
  };
  provider.addEventListener('change', renderSetup); renderSetup();
  const examples = [
    ['문서 목록 확인', 'Sheet에서 내가 접근할 수 있는 문서 목록을 보여줘.'],
    ['현재 문서로 대시보드 만들기', `Sheet의 ‘${documentTitle}’ 문서(${documentUrl})를 읽고 탭과 데이터 구조를 확인해줘. 실제 데이터에 맞는 주요 지표와 차트를 골라 인터랙티브 대시보드를 만들어줘.`],
    ['분석 결과를 시트에 저장', `Sheet의 ‘${documentTitle}’ 문서(${documentUrl})를 분석하고, 같은 문서에 ‘분석 요약’ 탭을 추가해서 결과를 기록해줘.`],
  ];
  const body = h('div', { class: 'mcp-guide' },
    h('p', { class: 'muted' }, 'ChatGPT 또는 Claude에 Sheet를 연결하면 대화로 문서를 읽고 분석하거나, 편집 가능한 시트에 결과를 기록할 수 있습니다.'),
    h('div', { class: 'col' }, h('label', { for: 'mcp-provider' }, '연결할 AI'), provider),
    h('section', { class: 'mcp-address' },
      h('label', { for: 'mcp-server-url' }, 'MCP 서버 URL'),
      h('div', { class: 'row' }, address, h('button', { class: 'primary', type: 'button', onclick: () => void copy(endpoint) }, '주소 복사'))),
    setup,
    h('section', {}, h('h3', {}, '2. 이렇게 요청해 보세요'),
      ...examples.map(([title, prompt]) => h('div', { class: 'mcp-example' },
        h('div', { class: 'row' }, h('strong', { class: 'grow' }, title), h('button', { class: 'link', type: 'button', 'aria-label': `${title} 예시 복사`, onclick: () => void copy(prompt) }, '예시 복사')),
        h('p', {}, prompt)))),
    h('section', { class: 'mcp-note' }, h('h3', {}, '사용 범위'),
      h('p', {}, '문서 목록과 시트 구조 확인, 셀 읽기·쓰기, 탭 추가를 지원합니다. 내 계정의 문서 권한과 보호 범위가 적용됩니다.'),
      h('p', {}, '한 번에 최대 3,000셀 읽기, 2,000셀 쓰기를 지원합니다. 수식은 계산 결과가 아닌 원문으로 전달됩니다. 수정 요청은 AI 서비스에서 승인이 필요할 수 있습니다.'),
      h('p', {}, '대시보드 표시는 연결한 AI 서비스의 기능에 따라 달라집니다. 원본 데이터가 바뀌면 ‘시트를 다시 읽어서 대시보드를 갱신해줘’라고 요청하세요.')),
    h('details', {}, h('summary', {}, '연결이 안 되나요?'),
      h('ul', {},
        h('li', {}, '서버 URL 끝에 /mcp가 포함되어 있는지 확인하세요.'),
        h('li', {}, '로그인이나 연결 요청이 만료되면 기존 허용 창을 닫고 연결할 AI 서비스에서 연결을 다시 시작하세요.'),
        h('li', {}, '문서가 보이지 않으면 연결한 Sheet 계정과 문서 공유 권한을 확인하세요.'),
        h('li', {}, '플러그인이나 커스텀 커넥터 메뉴가 보이지 않으면 해당 AI 계정의 지원 여부와 워크스페이스 관리자 정책을 확인하세요.'),
        h('li', {}, 'ChatGPT에서 연결했는데 도구가 보이지 않으면 Sheet 플러그인 설치 여부를 확인하고, 도구 목록을 새로고침한 뒤 새 대화에서 선택하세요.'))),
    links);
  void dialog('AI MCP 연결 가이드', body, [{ label: '닫기', value: 'close', primary: true }]);
  body.closest('.dlg')?.classList.add('mcp-dlg');
}

const UPDATES: [string, string[]][] = [
  ['2026-10-15', ['버전 기록: 이름 지정, 미리보기, 복원', 'Excel(.xlsx) 가져오기·내보내기, 홈에서 파일 업로드', '템플릿 갤러리, 휴지통(30일 보관)', '이메일로 파일 보내기, 공동작업자에게 이메일, @언급 알림, 알림 설정', '스프레드시트 설정(로캘·시간대·재계산), 수동 계산', '차트, 피봇 테이블, 이미지, SPARKLINE, 사용자 칩', '필터, 데이터 확인, 조건부 서식, 이름 범위/함수, 보호, 그룹']],
  ['2026-10-14', ['메뉴와 툴바를 Google Sheets 구성에 맞춰 확장', '병합, 테두리, 고정, 숨기기, 링크, 메모, 선택하여 붙여넣기', '함수 506개, 실시간 동시 편집, 댓글, 공유']],
];
export function updatesDialog(): void {
  dialog('업데이트', h('div', { class: 'col' }, ...UPDATES.map(([d, items]) => h('div', {}, h('b', {}, d), h('ul', {}, ...items.map(i => h('li', {}, i)))))), [{ label: '닫기', value: 'x', primary: true }]);
}
export async function feedbackDialog(docId: string): Promise<void> {
  const ta = h('textarea', { class: 'dlg-input', rows: 6, placeholder: '개선 의견이나 문제를 알려주세요' });
  if (!await dialog('Sheets 개선 돕기', h('div', { class: 'col' }, ta, h('p', { class: 'muted small' }, '보낸 의견은 관리자에게 전달됩니다.')), [{ label: '취소', value: false }, { label: '보내기', value: true, primary: true }])) return;
  if (!ta.value.trim()) return;
  await api('POST', '/api/feedback', { body: ta.value.trim(), doc: docId, page: location.pathname });
  toast('의견을 보냈습니다. 감사합니다');
}

// ----- File > Security restrictions -----
export interface Restrictions { viewersNoDownload?: boolean; editorsNoShare?: boolean }
export async function restrictionsDialog(x: PanelCtx, owner: boolean): Promise<void> {
  const cur = (x.model.props.restrictions ?? {}) as Restrictions;
  const a = h('input', { type: 'checkbox', checked: !!cur.viewersNoDownload, disabled: !owner }), b = h('input', { type: 'checkbox', checked: !!cur.editorsNoShare, disabled: !owner });
  const ok = await dialog('보안 제한사항', h('div', { class: 'col' },
    h('label', { class: 'row' }, a, ' 뷰어 및 댓글 작성자에게 다운로드, 인쇄, 복사 옵션 표시 안함'),
    h('label', { class: 'row' }, b, ' 편집자가 권한을 변경하고 공유할 수 없음'),
    h('p', { class: 'muted small' }, owner ? '소유자만 변경할 수 있습니다.' : '이 문서에 적용된 제한사항입니다. 소유자만 변경할 수 있습니다.')),
  owner ? [{ label: '취소', value: false }, { label: '저장', value: true, primary: true }] : [{ label: '닫기', value: false }]);
  if (!ok || !owner) return;
  const next: Restrictions = {}; if (a.checked) next.viewersNoDownload = true; if (b.checked) next.editorsNoShare = true;
  x.submit({ t: 'docprops', patch: { restrictions: Object.keys(next).length ? next : null } });
  toast('보안 제한사항을 저장했습니다');
}
